import { useState, useCallback, useEffect, useMemo } from 'react';
import { Search, Plus, Edit2, RefreshCw, ArrowRight, PhoneIncoming, AlertTriangle, X, Check, Building2, Copy, Terminal } from 'lucide-react';
import { useSortable, SortTh } from '../hooks/useSortable';
import { listDids, listSites, assignSite, listPlatforms, getTeamsUsers, assignTeamsNumber, getRoutingProfiles, listCarriers, setDidCarrier } from '../services/portalApi';

// Map a DB DID row to our local number shape
function mapDid(d) {
  return {
    id:            String(d.didId),
    did:           d.e164,
    location:      d.description || d.countryCodeA3 || '—',
    country:       d.countryCodeA3 || '',
    customerName:  d.customerName  || null,
    customerId:    d.customerId    || null,
    siteId:        d.siteId        ?? null,
    siteName:      d.siteName      ?? null,
    carrierId:     d.carrierId     ?? null,
    carrierName:   d.carrierName   ?? null,
    lastSynced:    d.lastSynced    || null,
    status:        'active',
    cnam:          '',
    teamsUserId:   d.teamsUserId   || null,
    teamsUserName: d.teamsUserName || null,
    teamsUserUpn:  d.teamsUserUpn  || null,
    zoomUserId:    d.zoomUserId    || null,
    zoomUserName:  d.zoomUserName  || null,
  };
}

const STATUS_BADGE = {
  active:    <span className="badge badge-green">Active</span>,
  suspended: <span className="badge badge-red">Suspended</span>,
  porting:   <span className="badge badge-amber">Porting In</span>,
  reserved:  <span className="badge badge-gray">Reserved</span>,
};


function EditModal({ number, onClose, onSave }) {
  const [form, setForm] = useState({
    cnam: number.cnam,
    status: number.status,
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">Edit Number</div>
            <div style={{ fontSize: 12, color: 'var(--muted-3)', marginTop: 2, fontFamily: 'monospace' }}>{number.did}</div>
          </div>
          <button className="btn btn-secondary btn-xs" onClick={onClose}><X size={12}/></button>
        </div>
        <div className="modal-body">
          <div className="alert alert-info">
            <PhoneIncoming size={14} style={{ flexShrink: 0 }} />
            <span>Number sourced via <strong>Voxbone</strong> — changes here update CNAM only. Platform assignment is done inline in the number table.</span>
          </div>

          <div className="form-group">
            <label className="form-label">CNAM (Caller ID Name)</label>
            <input className="form-input" value={form.cnam} onChange={e => setForm({...form, cnam: e.target.value})} placeholder="Display name for outbound calls" />
            <div className="form-hint">Max 15 characters · displayed on outbound caller ID</div>
          </div>

          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-input select-input form-select" value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="reserved">Reserved</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onSave(form)}>
            <Check size={13}/> Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

function PortModal({ onClose }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ numbers: '', provider: '', account: '', pin: '', contact: '', date: '' });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">Port Numbers In</div>
            <div style={{ fontSize: 12, color: 'var(--muted-3)', marginTop: 2 }}>Step {step} of 3 — {step === 1 ? 'Number Details' : step === 2 ? 'Authorisation' : 'Review & Submit'}</div>
          </div>
          <button className="btn btn-secondary btn-xs" onClick={onClose}><X size={12}/></button>
        </div>
        <div className="modal-body">
          {step === 1 && (
            <>
              <div className="alert alert-info">
                <AlertTriangle size={14}/>
                <span>Numbers are ported in via <strong>Voxbone</strong>. Once submitted, a FOC date is typically provided within 2 business days.</span>
              </div>
              <div className="form-group">
                <label className="form-label">Numbers to Port (one per line)</label>
                <textarea className="form-input" rows={5} placeholder={"+14155559001\n+14155559002\n+14155559003"} value={form.numbers} onChange={e => setForm({...form, numbers: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Losing Carrier</label>
                <input className="form-input" placeholder="e.g. AT&T, Verizon, Lumen..." value={form.provider} onChange={e => setForm({...form, provider: e.target.value})}/>
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <div className="form-group">
                <label className="form-label">BTN / Account Number</label>
                <input className="form-input" placeholder="Billing account number" value={form.account} onChange={e => setForm({...form, account: e.target.value})}/>
              </div>
              <div className="form-group">
                <label className="form-label">PIN / Passcode</label>
                <input className="form-input" type="password" placeholder="Account PIN" value={form.pin} onChange={e => setForm({...form, pin: e.target.value})}/>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Authorised Contact Name</label>
                  <input className="form-input" placeholder="Full name" value={form.contact} onChange={e => setForm({...form, contact: e.target.value})}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Requested FOC Date</label>
                  <input className="form-input" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})}/>
                </div>
              </div>
            </>
          )}
          {step === 3 && (
            <>
              <div className="alert alert-success">
                <Check size={14}/>
                <span>Review details below and click Submit to initiate the port-in order with Voxbone.</span>
              </div>
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <tbody>
                  {[['Numbers', form.numbers.split('\n').filter(Boolean).join(', ')], ['Carrier', form.provider], ['BTN', form.account], ['Contact', form.contact], ['FOC Date', form.date]].map(([k, v]) => (
                    <tr key={k} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '8px 0', color: 'var(--muted-2)', width: 130 }}>{k}</td>
                      <td style={{ padding: '8px 0', color: 'var(--text-2)' }}>{v || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
        <div className="modal-footer">
          {step > 1 && <button className="btn btn-secondary" onClick={() => setStep(step - 1)}>Back</button>}
          <button className="btn btn-secondary" onClick={onClose} style={{ marginRight: 'auto' }}>Cancel</button>
          {step < 3
            ? <button className="btn btn-primary" onClick={() => setStep(step + 1)}>Next <ArrowRight size={13}/></button>
            : <button className="btn btn-primary" onClick={onClose}><Check size={13}/> Submit Port Order</button>
          }
        </div>
      </div>
    </div>
  );
}

function AssignSiteModal({ sites, selectedCount, onClose, onConfirm }) {
  const [siteId, setSiteId]     = useState('');
  const [assigning, setAssigning] = useState(false);

  const handleConfirm = async () => {
    setAssigning(true);
    try {
      await onConfirm(siteId === 'none' ? null : siteId === '' ? null : Number(siteId));
    } catch {
      setAssigning(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">Assign to Site</div>
            <div style={{ fontSize: 12, color: 'var(--muted-3)', marginTop: 2 }}>{selectedCount} number{selectedCount !== 1 ? 's' : ''} selected</div>
          </div>
          <button className="btn btn-secondary btn-xs" onClick={onClose}><X size={12}/></button>
        </div>
        <div className="modal-body">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Site</label>
            <select className="form-input select-input" value={siteId} onChange={e => setSiteId(e.target.value)}>
              <option value="">— Select site —</option>
              <option value="none">Remove from site</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={assigning || siteId === ''}
          >
            {assigning
              ? <RefreshCw size={12} style={{ animation: 'spin 0.8s linear infinite' }}/>
              : <Building2 size={12}/>
            }
            {siteId === 'none' ? 'Remove from Site' : 'Assign to Site'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PsCommandModal({ identity, phoneNumber, psCommand, permissionMissing, permissionName, onClose }) {
  const [copied, setCopied] = useState(false);
  const copyCmd = () => {
    navigator.clipboard.writeText(psCommand).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Terminal size={16} style={{ color: 'var(--info)' }}/> Teams Direct Routing Assignment
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted-3)', marginTop: 2 }}>
              Assigning <span style={{ fontFamily: 'monospace', color: 'var(--text-2)' }}>{phoneNumber}</span> to <span style={{ color: 'var(--info)' }}>{identity}</span>
            </div>
          </div>
          <button className="btn btn-secondary btn-xs" onClick={onClose}><X size={12}/></button>
        </div>
        <div className="modal-body">
          {permissionMissing ? (
            <div className="alert" style={{ background: '#1c0a00', border: '1px solid #b4530055', marginBottom: 16 }}>
              <AlertTriangle size={14} style={{ color: 'var(--warn)', flexShrink: 0 }}/>
              <div style={{ fontSize: 12 }}>
                <span style={{ color: 'var(--warn)', fontWeight: 600 }}>Permission not yet granted: </span>
                <code style={{ fontFamily: 'monospace', color: '#fcd34d' }}>{permissionName}</code>
                <div style={{ color: 'var(--muted)', marginTop: 4 }}>
                  Re-run <strong style={{ color: 'var(--text-2)' }}>Connect with Microsoft 365</strong> on the Platforms page to grant this permission automatically, or run the PowerShell commands below manually.
                </div>
              </div>
            </div>
          ) : (
            <div className="alert" style={{ background: '#0c1a2e', border: '1px solid #1e40af55', marginBottom: 16 }}>
              <AlertTriangle size={14} style={{ color: 'var(--warn)', flexShrink: 0 }}/>
              <span style={{ color: 'var(--info)', fontSize: 12 }}>
                Run the commands below in a <strong>Teams PowerShell</strong> session with Teams admin rights.
              </span>
            </div>
          )}

          <div style={{ fontSize: 12, color: 'var(--muted-2)', marginBottom: 6 }}>
            <strong style={{ color: 'var(--muted)' }}>Teams PowerShell</strong>
            <span style={{ marginLeft: 8 }}>Install: <code style={{ fontFamily: 'monospace', color: '#7dd3fc' }}>Install-Module MicrosoftTeams</code></span>
          </div>
          <div style={{
            background: '#0a0f1e', border: '1px solid var(--border)', borderRadius: 8,
            padding: '12px 14px', fontFamily: 'monospace', fontSize: 12,
            color: 'var(--text-2)', whiteSpace: 'pre', overflowX: 'auto', lineHeight: 1.7,
          }}>
            {psCommand}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          <button className="btn btn-primary" onClick={copyCmd}>
            {copied ? <Check size={13}/> : <Copy size={13}/>}
            {copied ? 'Copied!' : 'Copy Commands'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Numbers({ user, viewAsCustomer }) {
  const [numbers,         setNumbers]         = useState([]);
  const [routing,         setRouting]         = useState({});   // didId(string) -> profile
  const [sites,           setSites]           = useState([]);
  const [platforms,       setPlatforms]       = useState([]);
  const [teamsUsers,      setTeamsUsers]      = useState([]);
  const [localPlat,       setLocalPlat]       = useState({});   // didId -> platformId string
  const [localUser,       setLocalUser]       = useState({});   // didId -> msUserId string
  const [assigningIds,    setAssigningIds]    = useState(new Set());
  const [editingPlatRow,  setEditingPlatRow]  = useState(new Set());
  const [psResult,        setPsResult]        = useState(null);   // { identity, phoneNumber, psCommand }
  const [search,          setSearch]          = useState('');
  const [filterStatus,    setFilterStatus]    = useState('all');
  const [filterSite,      setFilterSite]      = useState('all');
  const [filterAssign,    setFilterAssign]    = useState('all');
  const [filterCarrier,   setFilterCarrier]   = useState('all');
  const [carriers,        setCarriers]        = useState([]);
  const [selected,        setSelected]        = useState(new Set());
  const [editTarget,      setEditTarget]      = useState(null);
  const [showPort,        setShowPort]        = useState(false);
  const [showAssignSite,  setShowAssignSite]  = useState(false);
  const [assigningSite,   setAssigningSite]   = useState(false);
  const [syncing,         setSyncing]         = useState(false);
  const [syncMsg,         setSyncMsg]         = useState(null);

  const loadNumbers = useCallback(async () => {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const [didsData, routingData] = await Promise.all([
        listDids(),
        getRoutingProfiles().catch(() => ({ routing: {} })),
      ]);
      const dids = didsData.dids || [];
      setNumbers(dids.map(mapDid));
      setRouting(routingData.routing || {});
      setSyncMsg({ type: 'success', text: `${dids.length} numbers loaded` });
    } catch (e) {
      setSyncMsg({ type: 'warn', text: `Load failed: ${e.message}` });
    } finally {
      setSyncing(false);
    }
  }, []);

  // Load from DB on mount
  useEffect(() => { loadNumbers(); }, [loadNumbers]);

  // Load sites in parallel
  useEffect(() => {
    listSites().then(data => setSites(data.sites || [])).catch(() => {});
  }, []);

  // Load carriers (for the Carrier column + filter + inline assign)
  useEffect(() => {
    listCarriers().then(data => setCarriers(data.carriers || [])).catch(() => {});
  }, []);

  const isAdmin = !!user?.isAdmin;
  async function handleSetCarrier(didId, carrierId) {
    const cid = carrierId === '' ? null : Number(carrierId);
    const carrierName = cid ? (carriers.find(c => c.id === cid)?.name || null) : null;
    setNumbers(prev => prev.map(n => n.id === String(didId) ? { ...n, carrierId: cid, carrierName } : n));
    try {
      await setDidCarrier(didId, cid);
    } catch (e) {
      setSyncMsg({ type: 'warn', text: `Carrier update failed: ${e.message}` });
      loadNumbers();
    }
  }

  // Load platforms scoped to current customer
  useEffect(() => {
    const custId = viewAsCustomer?.id;
    listPlatforms(custId).then(data => {
      setPlatforms(Array.isArray(data) ? data : (data.platforms || []));
    }).catch(() => {});
  }, [viewAsCustomer]);

  // Load Teams users when a Teams platform is present
  useEffect(() => {
    const hasTeams = platforms.some(p => p.name?.toLowerCase().includes('teams'));
    if (!hasTeams) { setTeamsUsers([]); return; }
    const custId = viewAsCustomer?.id;
    getTeamsUsers(custId).then(data => setTeamsUsers(data.users || [])).catch(() => {});
  }, [platforms, viewAsCustomer]);

  // Pre-populate platform/user dropdowns from persisted DB assignments
  useEffect(() => {
    if (numbers.length === 0 || platforms.length === 0) return;
    const teamsPlatform = platforms.find(p => p.name?.toLowerCase().includes('teams'));
    if (!teamsPlatform) return;
    const platId = String(teamsPlatform.id);
    setLocalPlat(prev => {
      const next = { ...prev };
      numbers.forEach(n => { if (n.teamsUserId && !prev[n.id]) next[n.id] = platId; });
      return next;
    });
    setLocalUser(prev => {
      const next = { ...prev };
      numbers.forEach(n => { if (n.teamsUserId && !prev[n.id]) next[n.id] = n.teamsUserId; });
      return next;
    });
  }, [numbers, platforms]);

  // Reset selection when filters change
  useEffect(() => { setSelected(new Set()); }, [filterSite, filterAssign, search]);

  const visibleNumbers = viewAsCustomer
    ? numbers.filter(n => n.customerId === viewAsCustomer.id || n.customerId === String(viewAsCustomer.id))
    : numbers;

  const filtered = visibleNumbers.filter(n => {
    const matchSearch  = !search || n.did.includes(search) || n.location.toLowerCase().includes(search.toLowerCase()) || (n.customerName || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus  = filterStatus === 'all' || n.status === filterStatus;
    const matchSite    = filterSite === 'all'
      ? true
      : filterSite === 'none'
      ? !n.siteId
      : n.siteId === Number(filterSite) || String(n.siteId) === filterSite;
    const isAssigned   = !!(n.teamsUserId || n.zoomUserId);
    const matchAssign  = filterAssign === 'all'           ? true
                       : filterAssign === 'unassigned'    ? !isAssigned
                       : filterAssign === 'teams'         ? !!n.teamsUserId
                       : filterAssign === 'zoom'          ? !!n.zoomUserId
                       : true;
    const matchCarrier = filterCarrier === 'all'  ? true
                       : filterCarrier === 'none' ? !n.carrierId
                       : n.carrierId === Number(filterCarrier);
    return matchSearch && matchStatus && matchSite && matchAssign && matchCarrier;
  });

  const numSortAccessors = useMemo(() => ({
    assigned: n => n.teamsUserName || n.zoomUserName || '',
    site:     n => n.siteName || '',
    carrier:  n => n.carrierName || '',
    platform: n => n.teamsUserName ? 'teams' : n.zoomUserName ? 'zoom' : '',
  }), []);
  const { sortKey: nSortKey, sortDir: nSortDir, handleSort: nHandleSort, sortedRows: sortedNumbers } = useSortable(filtered, numSortAccessors);

  const allFilteredSelected = filtered.length > 0 && filtered.every(n => selected.has(n.id));
  const someSelected = selected.size > 0 && !allFilteredSelected;

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelected(prev => {
        const next = new Set(prev);
        filtered.forEach(n => next.delete(n.id));
        return next;
      });
    } else {
      setSelected(prev => {
        const next = new Set(prev);
        filtered.forEach(n => next.add(n.id));
        return next;
      });
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSave = (form) => {
    setNumbers(prev => prev.map(n => n.id === editTarget.id ? { ...n, ...form } : n));
    setEditTarget(null);
  };

  const isTeamsPlatform = (platformId) => {
    const p = platforms.find(p => String(p.id) === String(platformId));
    return p ? p.name?.toLowerCase().includes('teams') : false;
  };

  const handleAssignTeams = async (n) => {
    const msUserId = localUser[n.id];
    if (!msUserId) return;
    const u = teamsUsers.find(u => u.msUserId === msUserId);
    setAssigningIds(prev => new Set([...prev, n.id]));
    try {
      const result = await assignTeamsNumber(msUserId, n.did, u?.upn, u?.phoneNumber || null, viewAsCustomer?.id || n.customerId);
      if (result.queued) {
        setNumbers(prev => prev.map(n2 => n2.id === n.id
          ? { ...n2, teamsUserId: msUserId, teamsUserName: result.teamsUserName || u?.displayName || msUserId, teamsUserUpn: result.teamsUserUpn || u?.upn || '' }
          : n2
        ));
        setEditingPlatRow(prev => { const next = new Set(prev); next.delete(n.id); return next; });
        setSyncMsg({ type: 'success', text: `Assigned ${n.did} to ${result.identity}` });
      } else {
        setPsResult({
          identity:          result.identity || u?.upn || msUserId,
          phoneNumber:       result.phoneNumber || n.did,
          psCommand:         result.psCommand,
          permissionMissing: result.permissionMissing,
          permissionName:    result.permissionName,
        });
      }
    } catch (e) {
      setSyncMsg({ type: 'warn', text: `Assignment failed: ${e.message}` });
    } finally {
      setAssigningIds(prev => { const next = new Set(prev); next.delete(n.id); return next; });
    }
  };

  const handleUnassignTeams = async (n) => {
    if (!n.teamsUserId) return;
    setAssigningIds(prev => new Set([...prev, n.id]));
    try {
      const result = await assignTeamsNumber(n.teamsUserId, '', n.teamsUserUpn || n.teamsUserId, n.did, viewAsCustomer?.id || n.customerId);
      if (result.queued || result.ok) {
        setNumbers(prev => prev.map(n2 => n2.id === n.id
          ? { ...n2, teamsUserId: null, teamsUserName: null, teamsUserUpn: null }
          : n2
        ));
        setLocalPlat(prev => { const next = { ...prev }; delete next[n.id]; return next; });
        setLocalUser(prev => { const next = { ...prev }; delete next[n.id]; return next; });
        setEditingPlatRow(prev => { const next = new Set(prev); next.delete(n.id); return next; });
        setSyncMsg({ type: 'success', text: `Unassigned ${n.did} from Teams` });
      } else {
        setPsResult({
          identity:          result.identity || n.teamsUserUpn || n.teamsUserId,
          phoneNumber:       '',
          psCommand:         result.psCommand,
          permissionMissing: result.permissionMissing,
          permissionName:    result.permissionName,
        });
      }
    } catch (e) {
      setSyncMsg({ type: 'warn', text: `Unassign failed: ${e.message}` });
    } finally {
      setAssigningIds(prev => { const next = new Set(prev); next.delete(n.id); return next; });
    }
  };

  const handleAssignSite = async (siteId) => {
    setAssigningSite(true);
    try {
      await assignSite(Array.from(selected), siteId);
      await loadNumbers();
      setSites(prev => {
        // Reload sites to get updated counts
        listSites().then(data => setSites(data.sites || [])).catch(() => {});
        return prev;
      });
      setSelected(new Set());
      setShowAssignSite(false);
      setSyncMsg({ type: 'success', text: `Updated site assignment for ${selected.size} number${selected.size !== 1 ? 's' : ''}` });
    } catch (e) {
      setSyncMsg({ type: 'warn', text: `Failed: ${e.message}` });
    } finally {
      setAssigningSite(false);
    }
  };

  const platformMap = Object.fromEntries(platforms.map(p => [p.id, p]));

  return (
    <div className="content">
      {/* Customer view banner */}
      {viewAsCustomer && (
        <div className="alert alert-info" style={{ marginBottom: 16, borderColor: '#2563eb44', background: 'var(--info-bg)' }}>
          <span style={{ color: 'var(--info)' }}>
            Showing numbers assigned to <strong style={{ color: 'var(--info)' }}>{viewAsCustomer.name}</strong> — {visibleNumbers.length} number{visibleNumbers.length !== 1 ? 's' : ''} of {numbers.length} total.
          </span>
        </div>
      )}

      {/* Stats row */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
        {[
          { label: 'Total Numbers', value: visibleNumbers.length, color: 'var(--info)' },
          { label: 'Active',        value: visibleNumbers.filter(n=>n.status==='active').length, color: 'var(--ok)' },
          { label: 'Suspended',     value: visibleNumbers.filter(n=>n.status==='suspended').length, color: 'var(--err)' },
          { label: 'Porting In',    value: visibleNumbers.filter(n=>n.status==='porting').length, color: 'var(--warn)' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <div>
            <div className="card-title">DID Inventory</div>
            <div className="card-sub">
              {viewAsCustomer
                ? `${visibleNumbers.length} numbers assigned to ${viewAsCustomer.name}`
                : `Sourced via Voxbone · ${numbers.length} numbers total`}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {syncMsg && (
              <span style={{
                fontSize: 12, padding: '3px 10px', borderRadius: 6,
                background: syncMsg.type === 'success' ? 'var(--ok-bg)' : 'var(--warn-bg)',
                color:      syncMsg.type === 'success' ? 'var(--ok)'   : 'var(--warn)',
                border:     `1px solid ${syncMsg.type === 'success' ? '#16653444' : 'var(--warn-border)'}`,
              }}>{syncMsg.text}</span>
            )}
            {selected.size > 0 && (
              <button
                className="btn btn-sm"
                style={{ background: 'var(--ok-bg)', color: 'var(--cyan)', border: '1px solid #0891b255' }}
                onClick={() => setShowAssignSite(true)}
                disabled={assigningSite}
              >
                <Building2 size={13}/> Assign to Site ({selected.size})
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={loadNumbers} disabled={syncing}>
              <RefreshCw size={13} style={syncing ? { animation: 'spin 0.8s linear infinite' } : undefined}/>
              {syncing ? 'Loading…' : 'Refresh'}
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowPort(true)}>
              <Plus size={13}/> Port Numbers In
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)' }}>
          <div className="filter-row">
            <div className="search-bar">
              <Search size={14} color="var(--muted-3)"/>
              <input placeholder="Search number, location, customer…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className="select-input" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="porting">Porting In</option>
            </select>
            <select className="select-input" value={filterSite} onChange={e => setFilterSite(e.target.value)}>
              <option value="all">All Sites</option>
              <option value="none">No Site</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select className="select-input" value={filterAssign} onChange={e => setFilterAssign(e.target.value)}>
              <option value="all">All Assignments</option>
              <option value="unassigned">Not Assigned</option>
              <option value="teams">Microsoft Teams</option>
              <option value="zoom">Zoom</option>
            </select>
            <select className="select-input" value={filterCarrier} onChange={e => setFilterCarrier(e.target.value)}>
              <option value="all">All Carriers</option>
              <option value="none">No Carrier</option>
              {carriers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <span style={{ fontSize: 12, color: 'var(--muted-3)', marginLeft: 'auto' }}>{filtered.length} results</span>
          </div>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 40, textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    ref={el => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleSelectAll}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
                <SortTh label="DID Number"    sortKey="did"         currentKey={nSortKey} currentDir={nSortDir} onSort={nHandleSort} />
                <SortTh label="Location"      sortKey="location"    currentKey={nSortKey} currentDir={nSortDir} onSort={nHandleSort} />
                <SortTh label="Country"       sortKey="country"     currentKey={nSortKey} currentDir={nSortDir} onSort={nHandleSort} />
                <SortTh label="Status"        sortKey="status"      currentKey={nSortKey} currentDir={nSortDir} onSort={nHandleSort} />
                <SortTh label="Assigned To"   sortKey="assigned"    currentKey={nSortKey} currentDir={nSortDir} onSort={nHandleSort} />
                <SortTh label="Site"          sortKey="site"        currentKey={nSortKey} currentDir={nSortDir} onSort={nHandleSort} />
                <SortTh label="Carrier"       sortKey="carrier"     currentKey={nSortKey} currentDir={nSortDir} onSort={nHandleSort} />
                <SortTh label="Platform / User" sortKey="platform"  currentKey={nSortKey} currentDir={nSortDir} onSort={nHandleSort} />
                <th>Route</th>
                <SortTh label="Last Synced"   sortKey="lastSynced"  currentKey={nSortKey} currentDir={nSortDir} onSort={nHandleSort} />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedNumbers.map(n => (
                <tr key={n.id} style={{ background: selected.has(n.id) ? 'rgba(6,182,212,0.06)' : undefined }}>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selected.has(n.id)}
                      onChange={() => toggleSelect(n.id)}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                  <td><span className="tag font-mono">{n.did}</span></td>
                  <td style={{ color: 'var(--muted)', fontSize: 12 }}>{n.location}</td>
                  <td style={{ color: 'var(--muted-2)', fontSize: 12 }}>{n.country || '—'}</td>
                  <td>{STATUS_BADGE[n.status]}</td>
                  <td>
                    {n.customerName
                      ? <span style={{ fontSize: 12, color: 'var(--info)' }}>{n.customerName}</span>
                      : <span style={{ fontSize: 12, color: 'var(--muted-3)' }}>Unassigned</span>
                    }
                  </td>
                  <td>
                    {n.siteName
                      ? (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 500,
                          background: 'var(--ok-bg)', color: 'var(--cyan)', border: '1px solid #0891b244',
                        }}>
                          {n.siteName}
                        </span>
                      )
                      : <span style={{ fontSize: 12, color: 'var(--muted-3)' }}>—</span>
                    }
                  </td>
                  <td>
                    {(() => {
                      const carrier = n.carrierId ? carriers.find(c => c.id === n.carrierId) : null;
                      const colorDot = (
                        <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', flexShrink: 0, background: carrier?.color || '#64748b' }} />
                      );
                      if (isAdmin) {
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {n.carrierId && colorDot}
                            <select
                              className="select-input"
                              value={n.carrierId || ''}
                              onChange={e => handleSetCarrier(n.id, e.target.value)}
                              style={{ fontSize: 12, padding: '3px 6px', minWidth: 120 }}
                            >
                              <option value="">— Carrier —</option>
                              {carriers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          </span>
                        );
                      }
                      return n.carrierName
                        ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--muted-2)' }}>{colorDot}{n.carrierName}</span>
                        : <span style={{ fontSize: 12, color: 'var(--muted-3)' }}>—</span>;
                    })()}
                  </td>
                  <td>
                    {n.zoomUserName && !editingPlatRow.has(n.id) ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 500,
                        background: '#1b3a6033', color: '#2d8cff', border: '1px solid #2d8cff44',
                      }}>
                        Zoom · {n.zoomUserName}
                      </span>
                    ) : n.teamsUserName && !editingPlatRow.has(n.id) ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 500,
                        background: 'var(--ok-bg)', color: 'var(--ok)', border: '1px solid #14532d44',
                      }}>
                        Teams · {n.teamsUserName}
                        <button
                          onClick={() => setEditingPlatRow(prev => new Set([...prev, n.id]))}
                          title="Change assignment"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ok)', padding: 0, display: 'flex', alignItems: 'center', opacity: 0.7 }}
                          onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                          onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}
                        >
                          <Edit2 size={10}/>
                        </button>
                      </span>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <select
                          className="select-input"
                          style={{ fontSize: 11, padding: '3px 6px', minWidth: 140 }}
                          value={localPlat[n.id] || ''}
                          onChange={e => {
                            const val = e.target.value;
                            setLocalPlat(prev => ({ ...prev, [n.id]: val }));
                            setLocalUser(prev => ({ ...prev, [n.id]: '' }));
                          }}
                        >
                          <option value="">— Platform —</option>
                          {n.teamsUserId && <option value="unassigned">Not Assigned</option>}
                          {platforms.map(p => (
                            <option key={p.id} value={String(p.id)}>{p.name}</option>
                          ))}
                        </select>
                        {localPlat[n.id] === 'unassigned' && (
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <span style={{ fontSize: 11, color: 'var(--muted)', flex: 1 }}>
                              Remove from {n.teamsUserName || 'Teams'}
                            </span>
                            <button
                              className="btn btn-xs"
                              disabled={assigningIds.has(n.id)}
                              onClick={() => handleUnassignTeams(n)}
                              style={{ whiteSpace: 'nowrap', background: 'var(--err-bg)', color: 'var(--err)', border: '1px solid #7f1d1d55' }}
                            >
                              {assigningIds.has(n.id)
                                ? <RefreshCw size={10} style={{ animation: 'spin 0.8s linear infinite' }}/>
                                : <X size={10}/>
                              }
                              Free Number
                            </button>
                          </div>
                        )}
                        {localPlat[n.id] && isTeamsPlatform(localPlat[n.id]) && (
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <select
                              className="select-input"
                              style={{ fontSize: 11, padding: '3px 6px', minWidth: 140, flex: 1 }}
                              value={localUser[n.id] || ''}
                              onChange={e => setLocalUser(prev => ({ ...prev, [n.id]: e.target.value }))}
                            >
                              <option value="">— Select user —</option>
                              {teamsUsers.map(u => (
                                <option key={u.msUserId || u.id} value={u.msUserId || u.id}>
                                  {u.displayName || u.upn}
                                </option>
                              ))}
                            </select>
                            <button
                              className="btn btn-primary btn-xs"
                              disabled={!localUser[n.id] || assigningIds.has(n.id)}
                              onClick={() => handleAssignTeams(n)}
                              style={{ whiteSpace: 'nowrap' }}
                            >
                              {assigningIds.has(n.id)
                                ? <RefreshCw size={10} style={{ animation: 'spin 0.8s linear infinite' }}/>
                                : <Check size={10}/>
                              }
                              Assign
                            </button>
                          </div>
                        )}
                        {n.teamsUserName && editingPlatRow.has(n.id) && (
                          <button
                            className="btn btn-secondary btn-xs"
                            onClick={() => setEditingPlatRow(prev => { const next = new Set(prev); next.delete(n.id); return next; })}
                            style={{ alignSelf: 'flex-start', fontSize: 10 }}
                          >
                            <X size={9}/> Cancel
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    {(() => {
                      const rp = routing[n.id];
                      if (!rp) return <span style={{ color: 'var(--muted-3)', fontSize: 12 }}>—</span>;
                      const active      = rp.activeRoute || 'unset';
                      const primaryPlat = rp.primaryPlatformId ? platformMap[rp.primaryPlatformId] : null;
                      const altPlat     = rp.altPlatformId     ? platformMap[rp.altPlatformId]     : null;
                      const primaryLabel = primaryPlat?.abbr || (rp.primaryPstnE164 ? 'PSTN' : null);
                      const altLabel     = altPlat?.abbr     || (rp.altPstnE164     ? 'PSTN' : null);
                      if (!primaryLabel && !altLabel) return <span style={{ color: 'var(--muted-3)', fontSize: 12 }}>—</span>;
                      return (
                        <div style={{ fontSize: 11 }}>
                          {primaryLabel && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: altLabel ? 3 : 0 }}>
                              <span style={{
                                width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                                background: active === 'primary' ? 'var(--ok)' : 'var(--muted-3)',
                                boxShadow: active === 'primary' ? '0 0 5px var(--ok)' : 'none',
                              }}/>
                              <span style={{ color: active === 'primary' ? 'var(--ok)' : 'var(--muted-2)', fontWeight: active === 'primary' ? 600 : 400 }}>
                                {primaryLabel}
                              </span>
                              {active === 'primary' && <span style={{ color: 'var(--muted-3)', fontSize: 10 }}>↑ active</span>}
                            </div>
                          )}
                          {altLabel && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{
                                width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                                background: active === 'alternative' ? 'var(--warn)' : 'var(--muted-3)',
                                boxShadow: active === 'alternative' ? '0 0 5px var(--warn)' : 'none',
                              }}/>
                              <span style={{ color: active === 'alternative' ? 'var(--warn)' : 'var(--muted-2)', fontWeight: active === 'alternative' ? 600 : 400 }}>
                                {altLabel}
                              </span>
                              {active === 'alternative' && <span style={{ color: 'var(--muted-3)', fontSize: 10 }}>↑ active</span>}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={{ color: 'var(--muted-3)', fontSize: 11, fontFamily: 'monospace' }}>
                    {n.lastSynced ? n.lastSynced.slice(0, 10) : '—'}
                  </td>
                  <td>
                    <button className="btn btn-secondary btn-xs" onClick={() => setEditTarget(n)}>
                      <Edit2 size={11}/> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editTarget && <EditModal number={editTarget} onClose={() => setEditTarget(null)} onSave={handleSave} />}
      {showPort && <PortModal onClose={() => setShowPort(false)} />}
      {psResult && (
        <PsCommandModal
          identity={psResult.identity}
          phoneNumber={psResult.phoneNumber}
          psCommand={psResult.psCommand}
          permissionMissing={psResult.permissionMissing}
          permissionName={psResult.permissionName}
          onClose={() => setPsResult(null)}
        />
      )}
      {showAssignSite && (
        <AssignSiteModal
          sites={sites}
          selectedCount={selected.size}
          onClose={() => setShowAssignSite(false)}
          onConfirm={handleAssignSite}
        />
      )}
    </div>
  );
}
